<template>
  <l-feature-group>
    <l-feature-group v-if="debug">
      <l-circle-marker
        v-for="coords in debugPointsCoords"
        :key="coords.lat * coords.lng"
        :lat-lng="coords"
        :radius="1"
        color="black"
        :opacity="0.5"
      />
    </l-feature-group>
    <l-rectangle
      v-if="debug"
      color="red"
      :fill="false"
      :weight="2"
      :bounds="latLonBounds"
      :interactive="false"
    />
  </l-feature-group>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from "vue";
import { LRectangle, LCircleMarker, LFeatureGroup } from "@vue-leaflet/vue-leaflet";
import L, { LatLng, LatLngBounds, SVGOverlay } from "leaflet";
import type { Map as LeafletMap } from "leaflet";
import proj4 from "proj4";
import TimeseriesStore from "@/stores/timeseries/timeseries-module";
import { BBOX } from "@/stores/dataset/raster-dataset-model";
import RasterDatasetStore from "@/stores/dataset/raster-dataset-module";
import {
  leafletProjection,
  projectionDefinitions,
  worldGeodeticProjection,
} from "@/plugins/proj4";
import { goToErrorPage } from "@/router";
import { ErrorKey } from "@/router/model";

interface ViewBox {
  east: number;
  south: number;
  west: number;
  north: number;
}

export default defineComponent ({
  name: 'RasterSVGLayer',
  components: {
    LRectangle,
    LCircleMarker,
    LFeatureGroup,
  },
  props: {
    map: { type: Object as PropType<LeafletMap>, required: true },
    currentZoom: { type: Number, required: true },
    showGrid: { type: Boolean, default: true },
    showPoints: { type: Boolean, default: false },
    debug: { type: Boolean, default: false },
  },
  emits: ['clickedLocationProcessed'],
  setup(props, { emit }) {
    const rasterDatasetStore = RasterDatasetStore;
    const bbox = computed<BBOX>(() => rasterDatasetStore.metadata!.bbox);
    const gridBounds = computed<BBOX>(() => rasterDatasetStore.metadata!.gridBounds);
    const rasterStep = computed<number>(() => rasterDatasetStore.metadata!.spatialResolution);

    const crs = computed<string>(() => rasterDatasetStore.metadata!.crs);
    const timeseriesStore = TimeseriesStore;
    const activeTilesSvgOverlay = ref<L.Layer | null>(null);
    const zoomLevelDetailed = 10;

    const emitClickedLocationProcessed = () => {
      //  for linter
      emit("clickedLocationProcessed");
    };

    function watchActiveCoords() {
      if (!props.map) return;

      activeTilesSvgOverlay.value?.remove();
      activeTilesSvgOverlay.value = buildActiveTilesSVG();

      if (props.currentZoom > zoomLevelDetailed) {
        activeTilesSvgOverlay.value.addTo(props.map);
      }
    }

    const svgBackground = computed<SVGOverlay>(() => {
      const points = [...borderPoints.value];
      const paths: string[] = [];
      const path = (content: string) => {
        return `<path d="${content}" fill="black" stroke-width="0" fill-opacity="20%"/>`;
      };

      let content = `M ${points[0][0]} ${points[0][1]} `;
      points.shift();
      points.forEach(([x, y]) => {
        content += `L ${x} ${y}`;
      });
      paths.push(path(content));

      const svgElement = buildSvgElement();
      svgElement.innerHTML = paths.join('');
      return L.svgOverlay(svgElement, latLonBounds.value, {
        opacity: 1,
        interactive: false,
      });
    });

    const svgRaster = computed<SVGOverlay>(() => {
      const points: string[] = [];
      if (props.showPoints) {
        const point = (x: number, y: number) =>
          `<circle cx="${x}" cy="${y}" r="${rasterStep.value / 2}" fill="blue" />`;
        for (
          let x = gridBounds.value.west;
          x <= gridBounds.value.east;
          x += approxStep.value
        ) {
          for (
            let y = gridBounds.value.south;
            y <= gridBounds.value.north;
            y += approxStep.value
          ) {
            const [xt, yt] = transformXY(x, y);
            points.push(point(xt, yt));
          }
        }
      }

      const paths: string[] = [];
      if (props.showGrid) {
        const path = (content: string) =>
          `<path d="${content}" stroke="black" fill="transparent" stroke-width="${
            rasterStep.value * 1.6
          }" stroke-opacity="10%"/>`;
        const pathContentPrefix = (i: number) => (i === 0 ? 'M' : 'L');

        // vertical lines
        for (
          let x = gridBounds.value.west;
          x <= gridBounds.value.east;
          x += rasterStep.value
        ) {
          let pathContent = '';
          let i = 0;
          getVerticalLine(x).forEach(([x, y]) => {
            pathContent += `${pathContentPrefix(i)} ${x} ${y} `;
            i += 1;
          });
          paths.push(path(pathContent));
        }

        // horizontal lines
        for (
          let y = gridBounds.value.south;
          y <= gridBounds.value.north;
          y += rasterStep.value
        ) {
          let pathContent = '';
          let i = 0;
          getHorizontalLine(y).forEach(([x, y]) => {
            pathContent += `${pathContentPrefix(i)} ${x} ${y} `;
            i += 1;
          });
          paths.push(path(pathContent));
        }
      }

      const svgElement = buildSvgElement();
      svgElement.innerHTML = points.join('') + paths.join('');

      return L.svgOverlay(svgElement, latLonBounds.value, {
        opacity: 1,
        interactive: false,
      });
    });

    const buildActiveTilesSVG = (): SVGOverlay => {
      const paths = [];
      for (const indexString of activeTiles.value) {
        const indexes = JSON.parse(indexString) as [number, number];
        const [centerX, centerY] = [
          gridBounds.value.west + rasterStep.value * indexes[0],
          gridBounds.value.south + rasterStep.value * indexes[1],
        ];
        const size = rasterStep.value / 2;
        const [x1t, y1t] = transformXY(centerX - size, centerY - size);
        const [x2t, y2t] = transformXY(centerX + size, centerY - size);
        const [x3t, y3t] = transformXY(centerX + size, centerY + size);
        const [x4t, y4t] = transformXY(centerX - size, centerY + size);
        paths.push(
          `<path d="M${x1t} ${y1t} L ${x2t} ${y2t} L ${x3t} ${y3t} L ${x4t} ${y4t} Z"
                      fill="blue" fill-opacity="20%"/>`
        );
      }
      const svgElement = buildSvgElement();
      svgElement.innerHTML = paths.join('');
      return L.svgOverlay(svgElement, latLonBounds.value, {
        opacity: 1,
        interactive: false,
      });
    };

    const buildSvgElement = (): SVGSVGElement => {
      const svgElement = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );

      svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      svgElement.setAttribute(
        'viewBox',
        `${viewBox.value.west} ${viewBox.value.south} ${
          viewBox.value.east - viewBox.value.west
        } ${viewBox.value.north - viewBox.value.south}`
      );
      // // set background blue to verify shape of viewBox
      // svgElement.setAttribute(
      //   'style',
      //   'stroke-width: 0px; background-color: blue;'
      // );

      return svgElement as SVGSVGElement;
    };

    const activeTiles = computed<Set<string>>(() => {
      const activeTiles = new Set<string>();
      for (const point of activeCoords.value) {
        const [x, y] = proj4(worldGeodeticProjection, gridProjection.value, [
          point.lng,
          point.lat,
        ]);
        const indexes = [
          Math.round((x - gridBounds.value.west) / rasterStep.value),
          Math.round((y - gridBounds.value.south) / rasterStep.value),
        ] as [number, number];
        const indexesString = JSON.stringify(indexes);
        if (!activeTiles.has(indexesString)) {
          activeTiles.add(indexesString);
        }
      }
      return activeTiles;
    });

    const viewBox= computed<ViewBox> (() => {
      // transform bounds of svg from global coordinates to leaflet coordinates
      const southWest = proj4(worldGeodeticProjection, leafletProjection, [
        latLonBounds.value.getWest(),
        latLonBounds.value.getSouth(),
      ]);
      const northEast = proj4(worldGeodeticProjection, leafletProjection, [
        latLonBounds.value.getEast(),
        latLonBounds.value.getNorth(),
      ]);

      return {
        east: northEast[0],
        south: southWest[1],
        west: southWest[0],
        north: northEast[1],
      };
    });

    const debugPointsCoords = computed<LatLng[]>(() =>  {
      const coords = [];
      for (
        let x = gridBounds.value.west;
        x <= gridBounds.value.east;
        x += approxStep.value
      ) {
        for (
          let y = gridBounds.value.south;
          y <= gridBounds.value.north;
          y += approxStep.value
        ) {
          const proj = proj4(gridProjection.value, worldGeodeticProjection, [
            x,
            y,
          ]);
          coords.push(new LatLng(proj[1], proj[0]));
        }
      }
      return coords;
    });

    const getVerticalLine = (x: number): [number, number][] => {
      const points = [];
      for (
        let y = gridBounds.value.south;
        y <= gridBounds.value.north + approxStep.value;
        y += approxStep.value
      ) {
        let yCorrected = y;
        if (y === gridBounds.value.south) {
          yCorrected = gridBounds.value.south - rasterStep.value / 2;
        }
        if (y >= gridBounds.value.north) {
          yCorrected = gridBounds.value.north + rasterStep.value / 2;
        }
        points.push(transformXY(x, yCorrected));
      }
      return points;
    };

    const getHorizontalLine = (y: number): [number, number][] => {
      const points = [];
      for (
        let x = gridBounds.value.west;
        x <= gridBounds.value.east + approxStep.value;
        x += approxStep.value
      ) {
        let xCorrected = x;
        if (x === gridBounds.value.west) {
          xCorrected = gridBounds.value.west - rasterStep.value / 2;
        }
        if (x >= gridBounds.value.east) {
          xCorrected = gridBounds.value.east + rasterStep.value / 2;
        }
        points.push(transformXY(xCorrected, y));
      }
      return points;
    };

    const borderPoints = computed<[number, number][]>(() => {
      const westernLine = getVerticalLine(
        gridBounds.value.west - rasterStep.value / 2
      );
      const easternLine = getVerticalLine(
        gridBounds.value.east + rasterStep.value / 2
      );
      const northernLine = getHorizontalLine(
        gridBounds.value.north + rasterStep.value / 2
      );
      const southernLine = getHorizontalLine(
        gridBounds.value.south - rasterStep.value / 2
      );

      return westernLine.concat(
        northernLine,
        easternLine.reverse(),
        southernLine.reverse()
      );
    });

    function transformXY(x: number, y: number): [number, number] {
      // transform coordinate from austrian gridProjection to leaflet gridProjection
      const [xt, yt] = proj4(gridProjection.value, leafletProjection, [x, y]);
      return [
        Math.round(xt),
        // inverse y coordinate because svg coordinate system is upside down
        Math.round(viewBox.value.south + viewBox.value.north - yt),
      ];
    }

    const activeCoords = computed<LatLng[]>(() => {
      return Object.values(timeseriesStore.activeCoords);
    });

    const gridProjection = computed<string>(() => {
      if (!Object.keys(projectionDefinitions).includes(crs.value)) {
        console.error(`The gridProjection ${crs.value} of the dataset is unknown`);
        goToErrorPage(ErrorKey.VISUALIZATION_ERROR);
      }
      return crs.value;
    });

    const latLonBounds = computed<LatLngBounds>(() => {
      return L.latLngBounds([
        new LatLng(bbox.value.south, bbox.value.west),
        new LatLng(bbox.value.north, bbox.value.east),
      ]);
    });

    const approxStep = computed<number>(() => {
      // the grid cannot be drawn 100% precise due to performance reasons
      // because of that the grid is approximated with multiple straight lines instead of a curve
      // length of such a straight part of a horizontal/vertical line in m
      return 10000;
    });

    watch(
      () => props.map,
      (map) => {
        if (!map) return;

        svgBackground.value.addTo(map);
        activeTilesSvgOverlay.value = buildActiveTilesSVG();
        if (activeTilesSvgOverlay.value) {
          activeTilesSvgOverlay.value.addTo(map);
        }
      },
      { immediate: true }
    );

    watch(() => props.currentZoom, (newZoom) => {
      if (newZoom < zoomLevelDetailed) {
        if (props.map.hasLayer(svgRaster.value)) {
          svgRaster.value.remove();
        }
        if (activeTilesSvgOverlay.value && props.map.hasLayer(activeTilesSvgOverlay.value as L.Layer)) {
          activeTilesSvgOverlay.value?.remove();
        }
        if (svgBackground.value && !props.map.hasLayer(svgBackground.value)) {
          svgBackground.value.addTo(props.map);
        }
      } else {
        if (svgRaster.value && !props.map.hasLayer(svgRaster.value)) {
          svgRaster.value.addTo(props.map);
        }
        if (activeTilesSvgOverlay.value && !props.map.hasLayer(activeTilesSvgOverlay.value as L.Layer)) {
          activeTilesSvgOverlay.value.addTo(props.map);
        }
        if (svgBackground.value && props.map.hasLayer(svgBackground.value)) {
          svgBackground.value.remove();
        }
      }
    });

    // react when active coords change
    watch(
      () => timeseriesStore.activeCoords,
      () => watchActiveCoords(),
      { deep: true }
    );

    // react to zoom
    watch(
      () => props.currentZoom,
      () => watchActiveCoords()
    );

    // react when map becomes available
    watch(
      () => props.map,
      (map) => {
        if (map) {
          watchActiveCoords();
        }
      },
      { immediate: true }
    );

    return {
      emitClickedLocationProcessed,
      buildActiveTilesSVG,
      debugPointsCoords,
      latLonBounds,
    }
  },
});
</script>
